import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RootState } from '@next/core-lib/app';
import { ChipsFilterComponent } from '@next/core-lib/components';
import { ChipItem } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { delay, skipWhile, take } from 'rxjs/operators';
import { DosatorSetting } from 'src/app/settings/models/settings.model';
import { getDosatorSettings } from 'src/app/settings/store/selectors/settings.selectors';

import {
  FeedAdviceProperties,
  FeedAdviceType,
  FeedIntakeTypes,
  FeedingType,
  GeneticLineType,
  InputSystemType,
  PhaseTransitionTypes,
  TerminalBoar,
} from '../../models/feed-advice.model';
import * as actions from '../../store/feed-advice.actions';
import {
  getShowDosatorSetting,
  getShowEnergySetting,
  getShowLysineSetting,
} from '../../store/feed-advice.selectors';

@Component({
  selector: 'app-feed-advice-properties',
  templateUrl: './feed-advice-properties.component.html',
  styleUrls: ['./feed-advice-properties.component.scss'],
  imports: [
    NgIf,
    MatRadioGroup,
    ReactiveFormsModule,
    FormsModule,
    MatRadioButton,
    MatSelect,
    MatOption,
    NgFor,
    ChipsFilterComponent,
    MatInput,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FeedAdvicePropertiesComponent implements OnInit {
  @Input() feedAdviceType: FeedAdviceType;
  @Input() phaseTransition: PhaseTransitionTypes;
  displayedPhaseTransition: PhaseTransitionTypes;
  @Input() genders: ChipItem<number>[];
  displayedGenders: ChipItem<number>[];
  terminalBoars: string[] = Object.values(TerminalBoar);
  @Input() terminalBoar: string;
  @Input() geneticLine: GeneticLineType;
  @Input() hasValidation = true;
  genderValidationError: boolean;
  @Input() feedintake: FeedIntakeTypes;
  @Input() phases: number;
  @Input() inputSystem: InputSystemType;
  @Input() weaningWeight: number;
  @Input() weaningAge: number;
  @Input() rearingAge: number;
  @Input() isFilter = true;
  @Input() selectedDosatorSettingsId: number;
  @Input() feedingType?: FeedingType;
  @Output()
  propertiesChanged: EventEmitter<FeedAdviceProperties> = new EventEmitter<FeedAdviceProperties>();
  dosatorSettings$: Observable<DosatorSetting[]> = this.store.select(getDosatorSettings);
  selectedDosatorSetting: DosatorSetting = null;

  formGroup: FormGroup;
  formGroupRearingAge: FormGroup;
  showLysine: boolean;
  showEnergy: boolean;
  showDosator: boolean;
  selectedGenders: number[];

  geneticLines: GeneticLineType[] = Object.values(GeneticLineType);

  @ViewChild(ChipsFilterComponent) chipsFilterComponent: ChipsFilterComponent<number>;

  public readonly FeedAdviceTypeOptions = FeedingType;

  constructor(private readonly store: Store<RootState>) {}

  ngOnInit() {
    this.formGroup = new FormGroup({
      weaningWeight: new FormControl(this.weaningWeight, [Validators.required, Validators.min(1)]),
      weaningAge: new FormControl(this.weaningAge, [Validators.required, Validators.min(1)]),
    });

    this.formGroupRearingAge = new FormGroup({
      rearingAge: new FormControl(this.rearingAge, [Validators.required, Validators.min(1)]),
    });

    if (!this.feedAdviceType) {
      this.feedAdviceType = FeedAdviceType.FATTENING;
    }
    this.displayedGenders = this.getGendersPerSchemeType(this.feedAdviceType);
    this.displayedPhaseTransition = this.phaseTransition;

    if (this.isRearingGilt() && !this.feedingType) {
      this.feedingType = FeedingType.CDI;
    }

    this.store
      .select(getShowLysineSetting)
      .pipe(skipWhile((showLysine) => showLysine === null))
      .subscribe((showLysine) => {
        this.showLysine = showLysine ?? true;
      });

    this.store
      .select(getShowEnergySetting)
      .pipe(skipWhile((showEnergy) => showEnergy === null))
      .subscribe((showEnergy) => {
        this.showEnergy = showEnergy ?? true;
      });

    this.store
      .select(getShowDosatorSetting)
      .pipe(skipWhile((showDosator) => showDosator === null))
      .subscribe((showDosator) => {
        this.showDosator = showDosator ?? false;
      });

    if (!this.geneticLine) {
      this.geneticLine = null;
    }

    this.dosatorSettings$
      .pipe(
        skipWhile((dosatorSettings) => !dosatorSettings),
        take(1),
        delay(100),
      )
      .subscribe((dosatorSettings) => {
        const foundDosatorSetting = dosatorSettings.find(
          (dosatorSetting) => dosatorSetting.id === Number(this.selectedDosatorSettingsId),
        );
        if (foundDosatorSetting) {
          this.selectedDosatorSetting = foundDosatorSetting;
        } else if (dosatorSettings.length > 0) {
          this.selectedDosatorSetting = dosatorSettings[0];
          this.selectedDosatorSettingsId = this.selectedDosatorSetting.id;
          this.emitPropertiesChanged();
        } else {
          this.selectedDosatorSetting = null;
        }
      });
  }

  get weaningWeightFormControl(): AbstractControl {
    return this.formGroup.get('weaningWeight');
  }
  get weaningAgeFormControl(): AbstractControl {
    return this.formGroup.get('weaningAge');
  }
  get rearingAgeFormControl(): AbstractControl {
    return this.formGroupRearingAge.get('rearingAge');
  }

  selectGeneticLine($event: MatSelectChange) {
    this.geneticLine = $event.value;

    this.changeProperties();
  }

  changeProperties(selectedInput?: number | number[]) {
    let emit = true;

    if (this.selectedGenders === undefined && !this.isRearingGilt()) {
      emit = false;
    }

    if (selectedInput !== undefined) {
      if (selectedInput instanceof Number) {
        this.selectedGenders = [selectedInput as number];
      } else {
        this.selectedGenders = selectedInput as number[];
      }
    }

    if (this.selectedGenders) {
      this.displayedGenders = this.displayedGenders.map((gender) => ({
        value: gender.value,
        name: gender.name,
        selected: this.selectedGenders.findIndex((element) => element === gender.value) !== -1,
      }));
    }
    if (!this.geneticLine) {
      this.geneticLine = null;
    }

    switch (this.feedAdviceType) {
      case FeedAdviceType.REARING:
        this.displayedGenders = this.getGendersPerSchemeType(this.feedAdviceType);
        this.displayedGenders = this.displayedGenders.map((gender) => ({
          value: gender.value,
          name: gender.name,
          selected: true,
        }));
        break;
      case FeedAdviceType.FATTENING:
        if (this.displayedGenders.length !== 3) {
          this.displayedGenders = this.getGendersPerSchemeType(this.feedAdviceType);
        }
        break;
    }
    this.genderValidationError = !this.displayedGenders.some((gender) => gender.selected);

    if (emit) {
      this.emitPropertiesChanged();
    }
  }

  changeShowLysineSetting() {
    // save in settings service
    this.store.dispatch(actions.setShowLysineSetting({ save: true, showLysine: this.showLysine }));

    this.emitPropertiesChanged();
  }

  changeShowEnergySetting() {
    // save in settings service
    this.store.dispatch(actions.setShowEnergySetting({ save: true, showEnergy: this.showEnergy }));

    this.emitPropertiesChanged();
  }

  changeShowDosatorSetting() {
    // save in settings service
    this.store.dispatch(
      actions.setShowDosatorSetting({ save: true, showDosator: this.showDosator }),
    );

    this.emitPropertiesChanged();
  }

  private emitPropertiesChanged() {
    const val = {
      phaseTransition: this.displayedPhaseTransition,
      terminalBoar: this.terminalBoar,
      geneticLine: this.geneticLine,
      feedAdviceType: this.feedAdviceType,
      genders: !this.selectedGenders
        ? this.displayedGenders.map((gender: ChipItem<number>) => gender.name.toLowerCase())
        : this.displayedGenders
            .filter((gender: ChipItem<number>) =>
              this.selectedGenders
                ? this.selectedGenders.findIndex((element) => element === gender.value) !== -1
                : gender.selected,
            )
            .map((gender: ChipItem<number>) => gender.name.toLowerCase()),
      feedintake: this.feedintake,
      phases: this.phases,
      inputSystem: this.inputSystem,
      weaningWeight: this.formGroup.value.weaningWeight,
      weaningAge: this.formGroup.value.weaningAge,
      rearingAge: this.formGroupRearingAge.value.rearingAge,
      showLysine: this.showLysine,
      showEnergy: this.showEnergy,
      showDosator: this.showDosator,
      selectedDosatorSettingsId: this.selectedDosatorSettingsId,
      feedingType: this.feedingType,
    } as FeedAdviceProperties;
    this.propertiesChanged.emit(val);
  }

  isRearingGilt(): boolean {
    return this.feedAdviceType === FeedAdviceType.REARING;
  }

  isFatteningOrRearing(): boolean {
    return (
      this.feedAdviceType === FeedAdviceType.FATTENING ||
      this.feedAdviceType === FeedAdviceType.REARING
    );
  }

  isWeanedPiglets(): boolean {
    return this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS;
  }

  getGendersPerSchemeType(feedAdviceType: FeedAdviceType) {
    if (feedAdviceType === FeedAdviceType.REARING) {
      return [this.genders.find((x) => x.value === 3)];
    } else {
      return this.genders;
    }
  }

  setGenderValidationError(error: boolean) {
    this.genderValidationError = error;
  }

  clearProperties() {
    this.displayedPhaseTransition = null;
    this.terminalBoar = '';
    this.feedintake = null;
    this.geneticLine = null;
    this.phases = null;
    this.chipsFilterComponent.clear(<Event>{ preventDefault() {} });
    this.displayedGenders = this.getGendersPerSchemeType(FeedAdviceType.FATTENING);
    this.weaningWeight = null;
    this.weaningAge = null;
    this.rearingAge = null;
    this.showLysine = false;
    this.showEnergy = false;
    this.showDosator = false;
    this.selectedDosatorSettingsId = null;
  }

  shouldCalculateDosatorColumns(): boolean {
    return this.isRearingGilt() && this.showDosator && this.inputSystem === InputSystemType.KGDAY;
  }

  // Select a dosator settings
  selectDosatorSetting(dosatorSettingId: number) {
    this.selectedDosatorSettingsId = dosatorSettingId;
    this.changeProperties();
  }
}
